import { Component, Injectable, OnDestroy, OnInit } from '@angular/core';
import {NavbarComponent} from "../navbar/navbar.component";
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import { NgClass, NgFor, NgIf } from "@angular/common";
import {SelectDropDownModule} from "ngx-select-dropdown";
import {NavigationEnd, Router, RouterLink} from "@angular/router";
import {DeviceDetectorService} from "ngx-device-detector";
import { AuthService } from "../service/auth/auth.service";
import { RestService } from '../service/rest/rest.service';
import { NgxSliderModule } from '@angular-slider/ngx-slider';
import { LoggerService } from '../service/utils/logger.service';
import { Subject as SubjectModel } from '../model/subject';
import { Topic as TopicModel } from '../model/topic';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-question-pool',
  standalone: true,
  imports: [
    NavbarComponent,
    FormsModule,
    NgIf,
    NgFor,
    ReactiveFormsModule,
    NgClass,
    SelectDropDownModule,
    RouterLink,
    NgxSliderModule
  ],
  templateUrl: './question-pool.component.html',
  styleUrl: './question-pool.component.css'
})
@Injectable({
  providedIn: 'root',
})
export class QuestionPoolComponent implements OnInit, OnDestroy {

  // Make Array available in template
  Array = Array;

  private routerSub?: Subscription;

  questionPoolForm: FormGroup;
  categoryList:{ name: string, id: number }[] = [];
  subjectList: { name: string, id: number }[] = [];
  subjects: SubjectModel[] = []; // Store full subject objects with examQuestions
  subjectLoader: boolean = false;
  enableButtons: boolean = false;
  showSubjects: boolean = false;

  selectedCategory:{id:number,name:string} | undefined;
  selectedSubject:string | undefined;
  selectedSubjectObjects: { id: number, name: string }[] = []; // For dropdown binding
  testMode: 'study' | 'exam' = 'study'; // Default to study mode

  // Filter settings - each flag can be: 'none' (no filter), 'dontUse' (exclude), 'constrainTo' (only include)
  redFlagMode: 'none' | 'dontUse' | 'constrainTo' = 'none';
  yellowFlagMode: 'none' | 'dontUse' | 'constrainTo' = 'none';
  greenFlagMode: 'none' | 'dontUse' | 'constrainTo' = 'none';
  excludeHidden: boolean = true;   // Don't use hidden questions (default true for study mode)

  // Study-mode history filters
  onlyUnseen: boolean = false;
  onlyWrong: boolean = false;
  /** Ordered question IDs from recovery-mix / Train next prefill. */
  private pendingQuestionIds: number[] | null = null;

  // Study-mode topic filter (not shown for exam)
  topicLoader: boolean = false;
  topicLoadError: string = '';
  topicsBySubjectId: Record<number, TopicModel[]> = {};
  // topicId -> selected
  topicSelection: Record<number, boolean> = {};
  topicCountsLoader: boolean = false;
  topicQuestionCounts: Record<number, number> = {};
  subjectTopicsExpanded: Record<number, boolean> = {};

  protected getSelectedSubjectIds(): number[] {
    const selected = Array.isArray(this.selectedSubjectObjects) ? this.selectedSubjectObjects : [];
    return selected.map(s => s.id).filter(id => typeof id === 'number' && !Number.isNaN(id));
  }

  protected hasAnyTopicsForSelection(): boolean {
    const subjectIds = this.getSelectedSubjectIds();
    return subjectIds.some(id => (this.topicsBySubjectId[id]?.length || 0) > 0);
  }

  protected getTopicCount(topicId: number): number {
    return this.topicQuestionCounts[topicId] ?? 0;
  }

  protected isSubjectExpanded(subjectId: number): boolean {
    return this.subjectTopicsExpanded[subjectId] === true;
  }

  protected toggleSubjectExpanded(subjectId: number): void {
    this.subjectTopicsExpanded[subjectId] = !this.isSubjectExpanded(subjectId);
  }

  protected isTopicSelected(topicId: number): boolean {
    return this.topicSelection[topicId] !== false;
  }

  protected isSubjectTopicsChecked(subjectId: number): boolean {
    const topics = this.topicsBySubjectId[subjectId] || [];
    if (topics.length === 0) return false;
    return topics.every(t => this.isTopicSelected(t.id));
  }

  protected isSubjectTopicsIndeterminate(subjectId: number): boolean {
    const topics = this.topicsBySubjectId[subjectId] || [];
    if (topics.length === 0) return false;
    const selectedCount = topics.filter(t => this.isTopicSelected(t.id)).length;
    return selectedCount > 0 && selectedCount < topics.length;
  }

  protected async onSubjectTopicsToggle(subjectId: number, event: any) {
    const checked = !!event?.target?.checked;
    const topics = this.topicsBySubjectId[subjectId] || [];
    for (const t of topics) {
      this.topicSelection[t.id] = checked;
    }
    await this.applyFilters();
  }

  protected async onTopicToggle(topicId: number, event: any) {
    const checked = !!event?.target?.checked;
    this.topicSelection[topicId] = checked;
    await this.applyFilters();
  }

  private getAllTopicIdsForCurrentSelection(): number[] {
    if (this.testMode !== 'study') return [];
    const subjectIds = this.getSelectedSubjectIds();
    const ids: number[] = [];
    for (const sid of subjectIds) {
      for (const topic of (this.topicsBySubjectId[sid] || [])) {
        ids.push(topic.id);
      }
    }
    return ids;
  }

  private getSelectedTopicIdsForCurrentSelection(): number[] {
    const all = this.getAllTopicIdsForCurrentSelection();
    return all.filter(id => this.isTopicSelected(id));
  }

  /**
   * Only send the topic filter when it is explicitly constrained (i.e. not all selected).
   * Exception: if user deselects all topics, we must send an empty list to produce 0 questions.
   */
  private getTopicIdsFilterForRequest(): number[] | undefined {
    if (this.testMode !== 'study') return undefined;
    const all = this.getAllTopicIdsForCurrentSelection();
    if (all.length === 0) return undefined;
    const selected = this.getSelectedTopicIdsForCurrentSelection();
    if (selected.length === all.length) return undefined;
    return selected; // may be []
  }

  private async loadTopicsForSelectedSubjects(): Promise<void> {
    // Study mode only (not visible for exam)
    if (this.testMode !== 'study') {
      this.topicsBySubjectId = {};
      this.topicSelection = {};
      this.topicLoadError = '';
      this.topicLoader = false;
      this.topicQuestionCounts = {};
      this.topicCountsLoader = false;
      this.subjectTopicsExpanded = {};
      return;
    }

    const subjectIds = this.getSelectedSubjectIds();
    if (subjectIds.length === 0) {
      this.topicsBySubjectId = {};
      this.topicSelection = {};
      this.topicLoadError = '';
      this.topicLoader = false;
      this.topicQuestionCounts = {};
      this.topicCountsLoader = false;
      this.subjectTopicsExpanded = {};
      return;
    }

    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (!sessionId) {
        LoggerService.log('User is not authenticated');
        return;
      }

      this.topicLoader = true;
      this.topicLoadError = '';
      const topics = await this.restService.getTopicsForSubjects(sessionId, subjectIds);

      const grouped: Record<number, TopicModel[]> = {};
      for (const sid of subjectIds) grouped[sid] = [];

      if (topics === null) {
        this.topicLoadError = 'Could not load topics. Check that you have access to the selected subject(s).';
      } else {
        for (const t of topics) {
          if (!grouped[t.subjectId]) grouped[t.subjectId] = [];
          grouped[t.subjectId].push(t);
        }
      }

      // Sort by name for stable UI
      for (const sid of Object.keys(grouped)) {
        grouped[Number(sid)].sort((a, b) => a.name.localeCompare(b.name));
      }

      // Initialize new topics as selected (checked), and cleanup stale selections
      const currentTopicIds = new Set<number>();
      for (const list of Object.values(grouped)) {
        for (const topic of list) {
          currentTopicIds.add(topic.id);
          if (this.topicSelection[topic.id] == null) {
            this.topicSelection[topic.id] = true;
          }
        }
      }
      for (const key of Object.keys(this.topicSelection)) {
        const id = Number(key);
        if (!currentTopicIds.has(id)) {
          delete this.topicSelection[id];
        }
      }

      this.topicsBySubjectId = grouped;
      await this.loadTopicCountsForCurrentSelection();
    } catch (error) {
      LoggerService.error('Failed to fetch topics:', error);
      this.topicLoadError = 'Could not load topics.';
    } finally {
      this.topicLoader = false;
    }
  }

  private syncSubjectExpandedState(selected: { id: number; name: string }[]): void {
    const ids = new Set<number>(selected.map(s => s.id));

    // Remove stale entries
    for (const key of Object.keys(this.subjectTopicsExpanded)) {
      const id = Number(key);
      if (!ids.has(id)) {
        delete this.subjectTopicsExpanded[id];
      }
    }

    // Default: expanded when only one subject is selected, collapsed otherwise.
    const defaultExpanded = selected.length === 1;
    for (const subj of selected) {
      if (this.subjectTopicsExpanded[subj.id] == null) {
        this.subjectTopicsExpanded[subj.id] = defaultExpanded;
      }
    }
  }

  private async loadTopicCountsForCurrentSelection(): Promise<void> {
    if (this.testMode !== 'study') return;
    if (!this.selectedCategory || !this.selectedSubject) return;

    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (!sessionId) return;

      this.topicCountsLoader = true;
      // Use the "above" filters, but intentionally omit topicIds so we get counts for every topic.
      const filters: any = {
        redFlagMode: this.redFlagMode,
        yellowFlagMode: this.yellowFlagMode,
        greenFlagMode: this.greenFlagMode,
        excludeHidden: this.excludeHidden,
        onlyUnseen: this.onlyUnseen,
        onlyWrong: this.onlyWrong
      };
      this.topicQuestionCounts = await this.restService.loadTopicCounts(
        sessionId,
        this.selectedCategory.id,
        this.selectedSubject,
        filters
      );
    } catch (error) {
      LoggerService.error('Failed to fetch topic counts:', error);
      this.topicQuestionCounts = {};
    } finally {
      this.topicCountsLoader = false;
    }
  }

  // Helper method to get the label for a flag mode with "and only" logic
  getFlagLabel(flagType: 'red' | 'yellow' | 'green'): string {
    const mode = flagType === 'red' ? this.redFlagMode : 
                 flagType === 'yellow' ? this.yellowFlagMode : 
                 this.greenFlagMode;
    
    const hasConstraints = this.redFlagMode === 'constrainTo' || 
                          this.yellowFlagMode === 'constrainTo' || 
                          this.greenFlagMode === 'constrainTo';
    
    if (mode === 'none') return '-';
    if (mode === 'dontUse') return 'Don\'t use';
    
    if (mode === 'constrainTo') {
      // Check if this is the first constraint
      const isFirst = 
        (flagType === 'red' && this.redFlagMode === 'constrainTo') ||
        (flagType !== 'red' && this.redFlagMode !== 'constrainTo' && 
         ((flagType === 'yellow' && this.yellowFlagMode === 'constrainTo') ||
          (flagType === 'green' && this.greenFlagMode === 'constrainTo')));
      
      const flagName = flagType.charAt(0).toUpperCase() + flagType.slice(1);
      
      // Find if red is the first constraint
      if (this.redFlagMode === 'constrainTo') {
        if (flagType === 'red') return `Only ${flagName}`;
        return `and only ${flagName}`;
      }
      // Find if yellow is the first constraint
      if (this.yellowFlagMode === 'constrainTo') {
        if (flagType === 'yellow') return `Only ${flagName}`;
        return `and only ${flagName}`;
      }
      // Green must be first
      if (flagType === 'green') return `Only ${flagName}`;
    }
    
    return '-';
  }

  // Helper method to determine if a flag can toggle to a certain mode
  canToggleFlagMode(flagType: 'red' | 'yellow' | 'green', mode: 'none' | 'dontUse' | 'constrainTo'): boolean {
    const hasOtherConstraints = 
      (flagType !== 'red' && this.redFlagMode === 'constrainTo') ||
      (flagType !== 'yellow' && this.yellowFlagMode === 'constrainTo') ||
      (flagType !== 'green' && this.greenFlagMode === 'constrainTo');
    
    // If other flags have constraints, only allow 'none' and 'constrainTo'
    if (hasOtherConstraints && mode === 'dontUse') {
      return false;
    }
    
    return true;
  }

  protected mobile: boolean = false;
  protected amountQuestions: number = 50; // Default to 50 questions
  protected sliderOptions = {
    floor: 1,
    ceil: 100,
    showTicks: true,
    showSelectionBar: true,
    disabled: false
  };
  questionsCount: number = 0;
  examRouterLink: string[] = [];
  studyRouterLink: string[] = [];
  private questionsMetaRequestSeq: number = 0;
  /** When true, updateQuestionCountForMode must not overwrite amountQuestions (recreate flow). */
  private preserveAmountFromRecreate = false;

  constructor(private fb: FormBuilder,
              private authService: AuthService,
              private restService: RestService,
              private router: Router,
              private deviceService: DeviceDetectorService) {

    this.questionPoolForm = this.fb.group({
        category: ['',[Validators.required]],
        subject: ['',[Validators.required]]
      }
    );
    // When you are on the Question Pool page you’re explicitly configuring a NEW test,
    // so keep the UI free of “resume/cancel” prompts by clearing stale test state.
    this.restService.clearCurrentTestState();
    // Restore recreate settings from router state (Recreate from Saved tests) so getCourses() can apply them
    const nav = this.router.getCurrentNavigation();
    const state = nav?.extras?.state as { recreateSettings?: import('../service/rest/rest.service').RecreateSettings } | undefined;
    if (state?.recreateSettings) {
      this.restService.setRecreateSettings(state.recreateSettings);
    }
    this.getCourses();
    this.mobile = this.deviceService.isMobile();
    this.authService = authService;
  }

  ngOnInit(): void {
    this.routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.onNavigationEnd(e));
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
  }

  /** Restore selectedSubject/selectedSubjectObjects from stored comma-separated subject ids (call after getSubjectList). */
  private restoreSubjectsFromStorage(subjectIdsStr: string): void {
    const ids = subjectIdsStr.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !Number.isNaN(n));
    if (ids.length === 0) return;
    const ordered = this.subjectList.filter(s => ids.includes(s.id));
    if (ordered.length === 0) return;
    this.selectedSubjectObjects = ordered;
    this.selectedSubject = ordered.map(s => s.id).join(',');
    this.syncSubjectExpandedState(ordered);
  }

  /** When we navigate to the question-pool config page (e.g. from dashboard after a test), refetch courses and counts
   * so they reflect latest data (e.g. after an import or finishing a test). */
  private onNavigationEnd(event: NavigationEnd): void {
    const url = event.urlAfterRedirects ?? event.url ?? '';
    const isQuestionPoolConfig =
      url.includes('question-pool') &&
      !url.includes('question-pool-study') &&
      !url.includes('question-pool-exam') &&
      !url.includes('question-pool-results');
    if (!isQuestionPoolConfig) return;
    // Always refetch courses when landing on this page so we show fresh data (e.g. after import or component reuse).
    void this.getCourses();
  }

  async getCourses(): Promise<void> {
    try {
      let user = this.authService.getUser();
      const sessionId = user?.sid;
      if (sessionId) {
        // Use subscription-based courses (same as Settings page)
        const courses = await this.restService.getUserCourses(sessionId);
        if (courses) {
          this.categoryList = courses.map(course => ({
            name: course.name,
            id: course.id
          }));

          // Restore last selection from session so "New test" after a test shows updated counts
          if (this.categoryList.length > 0) {
            const recreate = this.restService.getRecreateSettings();
            const courseId = recreate?.courseId != null ? Number(recreate.courseId) : NaN;
            const subjectIds = recreate?.subjectIds ?? (recreate && (recreate as any).subjectId != null ? [(recreate as any).subjectId] : []);
            if (recreate && !Number.isNaN(courseId) && this.categoryList.some(c => Number(c.id) === courseId)) {
              this.selectedCategory = this.categoryList.find(c => Number(c.id) === courseId)!;
              this.testMode = recreate.mode === 'exam' ? 'exam' : 'study';
              await this.getSubjectList(this.selectedCategory.id);
              const subjects = subjectIds
                .map((id: number) => this.subjectList.find(s => Number(s.id) === id))
                .filter((s): s is { id: number; name: string } => s != null);
              if (subjects.length > 0) {
                this.selectedSubjectObjects = subjects;
                this.selectedSubject = subjectIds.join(',');
                this.syncSubjectExpandedState(this.selectedSubjectObjects);
              }
              if (recreate.amountQuestions != null) this.amountQuestions = recreate.amountQuestions;
              if (recreate.redFlagMode != null) this.redFlagMode = recreate.redFlagMode as 'none' | 'dontUse' | 'constrainTo';
              if (recreate.yellowFlagMode != null) this.yellowFlagMode = recreate.yellowFlagMode as 'none' | 'dontUse' | 'constrainTo';
              if (recreate.greenFlagMode != null) this.greenFlagMode = recreate.greenFlagMode as 'none' | 'dontUse' | 'constrainTo';
              if (recreate.excludeHidden != null) this.excludeHidden = recreate.excludeHidden;
              if (recreate.onlyUnseen != null) this.onlyUnseen = recreate.onlyUnseen;
              if (recreate.onlyWrong != null) this.onlyWrong = recreate.onlyWrong;
              if (recreate.questionIds?.length) {
                this.pendingQuestionIds = [...recreate.questionIds];
                this.amountQuestions = recreate.questionIds.length;
                this.restService.setQuestionIdsForResume(recreate.questionIds);
                this.restService.setMaxQuestions(recreate.questionIds.length);
                this.questionsCount = recreate.questionIds.length;
              }
              await this.loadTopicsForSelectedSubjects();
              if (recreate.topicIds != null) {
                const ids = new Set(recreate.topicIds);
                for (const key of Object.keys(this.topicSelection)) {
                  const id = Number(key);
                  this.topicSelection[id] = ids.has(id);
                }
              }
              this.preserveAmountFromRecreate = true;
              try {
                if (!recreate.questionIds?.length) {
                  await this.applyFilters();
                }
                if (recreate.amountQuestions != null && recreate.amountQuestions > 0) {
                  this.amountQuestions = recreate.amountQuestions;
                  this.restService.setMaxQuestions(this.amountQuestions);
                }
                if (recreate.questionIds?.length) {
                  this.amountQuestions = recreate.questionIds.length;
                  this.restService.setMaxQuestions(recreate.questionIds.length);
                  this.questionsCount = recreate.questionIds.length;
                }
              } finally {
                this.preserveAmountFromRecreate = false;
              }
              this.restService.clearRecreateSettings();
              return;
            }
            const storedCat = sessionStorage.getItem('atplane_qp_category');
            const storedSubj = sessionStorage.getItem('atplane_qp_subjects');
            const catId = storedCat ? parseInt(storedCat, 10) : NaN;
            const match = !Number.isNaN(catId) && this.categoryList.some(c => c.id === catId);
            if (match && storedSubj) {
              this.selectedCategory = this.categoryList.find(c => c.id === catId)!;
              await this.getSubjectList(this.selectedCategory.id);
              this.restoreSubjectsFromStorage(storedSubj);
              const ow = sessionStorage.getItem('atplane_qp_onlyWrong');
              const ou = sessionStorage.getItem('atplane_qp_onlyUnseen');
              if (ow !== null) this.onlyWrong = ow === 'true';
              if (ou !== null) this.onlyUnseen = ou === 'true';
              await this.loadTopicsForSelectedSubjects();
              await this.applyFilters();
            } else {
              this.selectedCategory = this.categoryList[0];
              void this.getSubjectList(this.selectedCategory.id);
            }
          } else {
            LoggerService.log('No courses found.');
          }
        } else {
          LoggerService.log('No courses found.');
        }
      } else {
        LoggerService.log('User is not authenticated');
      }
    } catch (error) {
      LoggerService.error('Failed to fetch courses:', error);
    }
  }


  // Removed getTopicList - question pools are no longer used
  // Subjects are now directly under courses


  async getSubjectList(courseId: number){
    this.subjectLoader = true;
    this.showSubjects = true;
    this.enableButtons = false;

    try {
      let user = this.authService.getUser();
      const sessionId = user?.sid;
      if (sessionId) {
        const subjects = await this.restService.getSubjects(sessionId, courseId);
        if (subjects) {
          // Store full subject objects for access to examQuestions
          this.subjects = subjects;
          // Map to dropdown format
          this.subjectList = subjects.map(subject => ({
            name: subject.subjectTitle,
            id: subject.subjectId
          }));
        } else {
          LoggerService.log('No subjects found.');
          this.subjects = [];
        }
      } else {
        LoggerService.log('User is not authenticated');
        this.subjects = [];
      }
    } catch (error) {
      LoggerService.error('Failed to fetch subjects:', error);
    }

    this.subjectLoader = false;
  }


  async loadQuestionsMeta(courseId: number, subjectIds: string) : Promise<Boolean> {

    try {
      let user = this.authService.getUser();
      const sessionId = user?.sid;
      if (sessionId) {
        const qMeta = await this.restService.loadQuestionsMeta(sessionId, courseId, subjectIds);
        
        if (qMeta != null && qMeta > 0) {
          this.questionsCount = qMeta;
          return true;
        }
        else {
          LoggerService.log('No Questions meta found.');
        }

      } else {
        LoggerService.log('User is not authenticated');
      }
    } catch (error) {
      LoggerService.error('Failed to fetch questions meta:', error);
    }
    return false;
  }

  // Called when any filter checkbox changes
  async onFilterChange() {
    // If the user manually tweaks color/hidden filters, disable "Only unseen" because it
    // conceptually overrides those filters.
    if (this.onlyUnseen) {
      this.onlyUnseen = false;
    }
    LoggerService.log(`Filter changed - Red: ${this.redFlagMode}, Yellow: ${this.yellowFlagMode}, Green: ${this.greenFlagMode}, ExcludeHidden: ${this.excludeHidden}, OnlyUnseen: ${this.onlyUnseen}, OnlyWrong: ${this.onlyWrong}`);
    await this.applyFilters();
  }

  // Toggle "Only unseen questions" (study mode only). Use ev.target.checked so we
  // always send the value that just changed (avoids ngModel timing vs (change)).
  async onOnlyUnseenChange(ev?: Event) {
    const checked = ev?.target ? (ev.target as HTMLInputElement).checked : this.onlyUnseen;
    this.onlyUnseen = checked;
    if (checked) {
      this.redFlagMode = 'none';
      this.yellowFlagMode = 'none';
      this.greenFlagMode = 'none';
      this.excludeHidden = false;
      this.onlyWrong = false;
    }
    LoggerService.log(`[onOnlyUnseenChange] onlyUnseen=${this.onlyUnseen}`);
    this.persistSelectionForRestore();
    await this.applyFilters();
  }

  // Toggle "Wrongly answered" (study mode only). Use ev.target.checked so we
  // always send the value that just changed.
  async onOnlyWrongChange(ev?: Event) {
    const checked = ev?.target ? (ev.target as HTMLInputElement).checked : this.onlyWrong;
    this.onlyWrong = checked;
    if (checked) {
      this.onlyUnseen = false;
    }
    LoggerService.log(`[onOnlyWrongChange] onlyWrong=${this.onlyWrong}`);
    this.persistSelectionForRestore();
    await this.applyFilters();
  }

  /** Persist category, subject, and filter checkboxes so "New test" after a test restores and refetches. */
  private persistSelectionForRestore(): void {
    if (!this.selectedCategory) return;
    try {
      sessionStorage.setItem('atplane_qp_category', String(this.selectedCategory.id));
      if (this.selectedSubject) sessionStorage.setItem('atplane_qp_subjects', this.selectedSubject);
      sessionStorage.setItem('atplane_qp_onlyWrong', String(this.onlyWrong));
      sessionStorage.setItem('atplane_qp_onlyUnseen', String(this.onlyUnseen));
    } catch {
      // ignore
    }
  }

  // Toggle flag mode between 'none', 'dontUse', 'constrainTo'
  toggleFlagMode(flagType: 'red' | 'yellow' | 'green') {
    const hasOtherConstraints = 
      (flagType !== 'red' && this.redFlagMode === 'constrainTo') ||
      (flagType !== 'yellow' && this.yellowFlagMode === 'constrainTo') ||
      (flagType !== 'green' && this.greenFlagMode === 'constrainTo');
    
    const currentMode = flagType === 'red' ? this.redFlagMode : 
                       flagType === 'yellow' ? this.yellowFlagMode : 
                       this.greenFlagMode;
    
    let nextMode: 'none' | 'dontUse' | 'constrainTo';
    
    if (hasOtherConstraints) {
      // When other flags have constraints, cycle only between 'none' and 'constrainTo'
      nextMode = currentMode === 'none' ? 'constrainTo' : 'none';
    } else {
      // Normal cycle: 'none' -> 'dontUse' -> 'constrainTo' -> 'none'
      const modeMap = {'none': 'dontUse', 'dontUse': 'constrainTo', 'constrainTo': 'none'};
      nextMode = modeMap[currentMode as keyof typeof modeMap] as any;
    }
    
    if (flagType === 'red') {
      this.redFlagMode = nextMode;
    } else if (flagType === 'yellow') {
      this.yellowFlagMode = nextMode;
    } else if (flagType === 'green') {
      this.greenFlagMode = nextMode;
    }
    
    this.onFilterChange();
  }

  // Pass filters to loadQuestionsMeta
  async applyFilters() {
    if (this.selectedCategory && this.selectedSubject) {
      const requestId = ++this.questionsMetaRequestSeq;
      const filters: any = {
        redFlagMode: this.redFlagMode,
        yellowFlagMode: this.yellowFlagMode,
        greenFlagMode: this.greenFlagMode,
        excludeHidden: this.excludeHidden,
        onlyUnseen: this.onlyUnseen,
        onlyWrong: this.onlyWrong
      };

      const topicIds = this.getTopicIdsFilterForRequest();
      if (topicIds !== undefined) {
        filters.topicIds = topicIds;
      }
      
      LoggerService.log(`[applyFilters] Loading questions with filters`);
      await this.loadQuestionsMetaWithFilters(this.selectedCategory.id, this.selectedSubject, filters, requestId);
      LoggerService.log(`[applyFilters] New questionsCount: ${this.questionsCount}`);
      this.enableButtons = this.questionsCount > 0;

      // Refresh per-topic counts (based on "above" filters)
      await this.loadTopicCountsForCurrentSelection();
      
      // Update slider and question count so the slider maximum (ceil) reflects the filtered count
      await this.updateQuestionCountForMode();
      
      // Cap amountQuestions to not exceed new questionsCount
      if (this.questionsCount > 0 && this.amountQuestions > this.questionsCount) {
        this.amountQuestions = this.questionsCount;
        this.restService.setMaxQuestions(this.amountQuestions);
        LoggerService.log(`[applyFilters] Capped amountQuestions to ${this.amountQuestions}`);
      }
      
      // Force a new slider options object so ngx-slider updates its maximum (ceil).
      // Use this.questionsCount explicitly so the slider max always matches the filtered count.
      const ceil = this.questionsCount != null && this.questionsCount > 0 ? this.questionsCount : 1;
      this.sliderOptions = {
        floor: 1,
        ceil,
        showTicks: this.sliderOptions.showTicks,
        showSelectionBar: this.sliderOptions.showSelectionBar,
        disabled: this.sliderOptions.disabled
      };

      LoggerService.log(`[applyFilters] Updated questionsCount to ${this.questionsCount}, amountQuestions: ${this.amountQuestions}, slider ceil: ${ceil}`);
    }
  }

  async loadQuestionsMetaWithFilters(courseId: number, subjectIds: string, filters: any, requestId?: number): Promise<Boolean> {
    try {
      let user = this.authService.getUser();
      const sessionId = user?.sid;
      if (sessionId) {
        const qMeta = await this.restService.loadQuestionsMeta(sessionId, courseId, subjectIds, filters);

        // Prevent out-of-order responses from overwriting newer filter selections
        if (requestId != null && requestId !== this.questionsMetaRequestSeq) {
          LoggerService.log(`[loadQuestionsMetaWithFilters] Ignoring stale response. requestId=${requestId}, latest=${this.questionsMetaRequestSeq}`);
          return false;
        }
        
        if (qMeta != null && qMeta > 0) {
          this.questionsCount = qMeta;
          this.enableButtons = true;
          return true;
        }
        else {
          LoggerService.log('No Questions meta found.');
          this.questionsCount = 0;
          this.enableButtons = false;
        }

      } else {
        LoggerService.log('User is not authenticated');
      }
    } catch (error) {
      LoggerService.error('Failed to fetch questions meta:', error);
    }
    return false;
  }

  onSubmit(){

  }

  onChangeCategory(value: any) {
    // Directly get subjects for the selected course (no question pools)
    if (value?.value?.id) {
      this.selectedCategory = value.value;
      this.getSubjectList(value.value.id);
    }
  }

  // Removed onChangeTopic - question pools are no longer used

  onAmountQuestionsChange(newMax: number): void {
    this.amountQuestions = newMax;
    this.restService.setMaxQuestions(newMax);
  }

  async onChangeSubject(value:any){
    if(!value?.value){
      this.enableButtons = false;
      this.questionsCount = 0;
      this.selectedSubjectObjects = [];
      this.topicsBySubjectId = {};
      this.topicSelection = {};
      this.topicLoadError = '';
      this.topicQuestionCounts = {};
      this.topicCountsLoader = false;
      this.subjectTopicsExpanded = {};
      return;
    }
    
    // Handle both single object (exam mode) and array (study mode)
    let selectedItems = Array.isArray(value.value) ? value.value : [value.value];
    
    if (selectedItems.length > 0 && this.selectedCategory != null) {
      // In exam mode, only allow one subject - keep only the first if multiple selected
      if (this.testMode === 'exam' && selectedItems.length > 1) {
        LoggerService.log(`[onChangeSubject] Exam mode: Multiple subjects selected, keeping only the first one`);
        selectedItems = [selectedItems[0]];
      }
      
      // Maintain the original order from subjectList instead of the order from the dropdown
      // This prevents the dropdown from reordering items when deselecting
      const orderedItems = this.subjectList.filter(subject => 
        selectedItems.some((item: { id: number, name: string }) => item.id === subject.id)
      );
      
      // Store the selected objects for dropdown binding in the correct order
      this.selectedSubjectObjects = orderedItems;
      this.syncSubjectExpandedState(orderedItems);
      
      // Validate and format subjectIds as a string
      const selectedIds = orderedItems.map((item: { id: number, name: string }) => item.id).join(',');
      this.selectedSubject = selectedIds;

      this.persistSelectionForRestore();

      if (this.selectedSubject && this.selectedCategory) {
        // Load topics for study mode (defaults to all selected)
        await this.loadTopicsForSelectedSubjects();

        // Check if any advanced filters are active
        const hasActiveAdvancedFilters =
          this.redFlagMode !== 'none' ||
          this.yellowFlagMode !== 'none' ||
          this.greenFlagMode !== 'none' ||
          this.excludeHidden ||
          this.onlyUnseen ||
          this.onlyWrong;
        
        if (hasActiveAdvancedFilters) {
          // If any filters are active, use applyFilters to properly recalculate
          LoggerService.log(`[onChangeSubject] Advanced filters are active, calling applyFilters()`);
          await this.applyFilters();
        } else {
          // Otherwise, just load the metadata with excludeHidden applied (+ optional topic filter)
          const filters: any = {
            redFlagMode: 'none',
            yellowFlagMode: 'none',
            greenFlagMode: 'none',
            excludeHidden: this.excludeHidden,
            onlyUnseen: this.onlyUnseen,
            onlyWrong: this.onlyWrong
          };
          const topicIds = this.getTopicIdsFilterForRequest();
          if (topicIds !== undefined) {
            filters.topicIds = topicIds;
          }
          const requestId = ++this.questionsMetaRequestSeq;
          await this.loadQuestionsMetaWithFilters(this.selectedCategory.id, this.selectedSubject, filters, requestId);
        }
      } else {
        this.questionsCount = 0;
        this.enableButtons = false;
      }

      // Update amountQuestions and slider based on mode
      await this.updateQuestionCountForMode();

      if (this.questionsCount > 0) {
        let questionId = this.restService.getFirstQuestionId();
        this.examRouterLink = ['/question-pool-exam/', ''+questionId, 'init'];
        this.studyRouterLink = ['/question-pool-study/', ''+questionId, 'init'];
      }
      this.enableButtons = this.questionsCount > 0;
    } else {
      this.enableButtons = false;
      this.questionsCount = 0;
      this.selectedSubjectObjects = [];
    }
  }

  async updateQuestionCountForMode() {
    if (this.testMode === 'exam') {
      // In exam mode, use exam_questions from the first selected subject
      const subjectIds = this.selectedSubject ? this.selectedSubject.split(',').map(id => parseInt(id)) : [];
      const subjectId = subjectIds.length > 0 ? subjectIds[0] : null;
      
      if (subjectId) {
        // Use local subjects array instead of restService.subjects
        const selectedSubject = this.subjects.find(s => s.subjectId === subjectId);
        LoggerService.log(`[updateQuestionCountForMode] Exam mode - subjectId: ${subjectId}, found subject: ${selectedSubject ? 'yes' : 'no'}`);
        LoggerService.log(`[updateQuestionCountForMode] Available subjects: ${this.subjects.length}`);
        
        if (selectedSubject) {
          LoggerService.log(`[updateQuestionCountForMode] Subject examQuestions: ${selectedSubject.examQuestions}`);
        }
        
        const examQuestions = selectedSubject?.examQuestions;
        
        // Check if examQuestions is a valid number (not null, undefined, or 0)
        // Use Number() to ensure we're comparing numbers, and check for NaN
        const examQuestionsNum = examQuestions != null ? Number(examQuestions) : null;
        
        if (examQuestionsNum != null && !isNaN(examQuestionsNum) && examQuestionsNum > 0) {
          // Ensure amountQuestions is always a valid number
          this.amountQuestions = Math.max(1, Math.floor(examQuestionsNum));
          this.restService.setMaxQuestions(this.amountQuestions);
          LoggerService.log(`[updateQuestionCountForMode] Using exam_questions: ${this.amountQuestions}`);
        } else {
          // Fallback to available questions if exam_questions not set
          // Ensure questionsCount is valid before using it
          const validQuestionsCount = (this.questionsCount != null && !isNaN(this.questionsCount) && this.questionsCount > 0) 
            ? this.questionsCount 
            : 0;
          
          // Only set if we have a valid count, otherwise keep current value
          if (validQuestionsCount > 0) {
            this.amountQuestions = validQuestionsCount;
            this.restService.setMaxQuestions(validQuestionsCount);
            LoggerService.log(`[updateQuestionCountForMode] exam_questions not set or invalid (value: ${examQuestions}), using questionsCount: ${validQuestionsCount}`);
          } else {
            LoggerService.log(`[updateQuestionCountForMode] exam_questions not set and questionsCount is invalid, keeping current amountQuestions: ${this.amountQuestions}`);
          }
        }
        
        // Ensure amountQuestions is always a valid number (prevent NaN)
        if (this.amountQuestions == null || isNaN(this.amountQuestions) || this.amountQuestions <= 0) {
          this.amountQuestions = 0;
          LoggerService.log(`[updateQuestionCountForMode] Fixed invalid amountQuestions, set to 0`);
        }
        
        // Disable slider in exam mode
        const validCeil = (this.questionsCount != null && !isNaN(this.questionsCount) && this.questionsCount > 0) 
          ? this.questionsCount 
          : 1;
        
        this.sliderOptions = {
          floor: 1,
          ceil: validCeil,
          showTicks: true,
          showSelectionBar: true,
          disabled: true
        };
      } else {
        LoggerService.log(`[updateQuestionCountForMode] No subjectId found, cannot set exam questions`);
        // Ensure amountQuestions is valid even if no subject
        if (this.amountQuestions == null || isNaN(this.amountQuestions) || this.amountQuestions <= 0) {
          this.amountQuestions = 0;
        }
      }
    } else {
      // In study mode, enable slider only when there are questions.
      const hasQuestions =
        this.questionsCount != null && !isNaN(this.questionsCount) && this.questionsCount > 0;

      if (!hasQuestions) {
        this.amountQuestions = 0;
        this.restService.setMaxQuestions(0);
        this.sliderOptions = {
          floor: 1,
          ceil: 1,
          showTicks: true,
          showSelectionBar: true,
          disabled: true
        };
        LoggerService.log(`[updateQuestionCountForMode] Study mode - no questions available, amountQuestions set to 0`);
        return;
      }

      const validQuestionsCount = this.questionsCount;
      const newAmount = validQuestionsCount >= 50 ? 50 : validQuestionsCount;

      // When restoring from Recreate, keep the recreated test's amount; do not overwrite with "Available" count
      if (this.preserveAmountFromRecreate && this.amountQuestions != null && this.amountQuestions > 0) {
        this.amountQuestions = Math.min(this.amountQuestions, validQuestionsCount);
        this.restService.setMaxQuestions(this.amountQuestions);
      } else {
        if (this.amountQuestions == null || isNaN(this.amountQuestions) || this.amountQuestions <= 0) {
          this.amountQuestions = newAmount;
        } else if (this.amountQuestions !== newAmount) {
          this.amountQuestions = newAmount;
        }
        this.restService.setMaxQuestions(this.amountQuestions);
      }

      // Enable slider in study mode - create new object reference
      this.sliderOptions = {
        floor: 1,
        ceil: validQuestionsCount,
        showTicks: true,
        showSelectionBar: true,
        disabled: false
      };

      LoggerService.log(`[updateQuestionCountForMode] Study mode - questionsCount: ${validQuestionsCount}, amountQuestions: ${this.amountQuestions}`);
    }
    
    LoggerService.log("Mode: " + this.testMode + ", new ceil: " + this.questionsCount + ", amountQuestions: " + this.amountQuestions);
  }

  async onModeChange() {
    // When switching to exam mode, reset color filters and ensure only one subject is selected
    if (this.testMode === 'exam') {
      // Topic filter is study-only
      this.topicsBySubjectId = {};
      this.topicSelection = {};
      this.topicLoadError = '';
      this.topicLoader = false;
      this.topicQuestionCounts = {};
      this.topicCountsLoader = false;

      // Reset all color filters to 'none'
      this.redFlagMode = 'none';
      this.yellowFlagMode = 'none';
      this.greenFlagMode = 'none';
      // History filters are study-only
      this.onlyUnseen = false;
      this.onlyWrong = false;
      
      // Uncheck "Don't use hidden questions" in exam mode
      this.excludeHidden = false;
      
      LoggerService.log(`[onModeChange] Exam mode: Color filters reset and excludeHidden set to false`);
      
      // Ensure only one subject is selected in exam mode
      if (this.selectedSubject) {
        const subjectIds = this.selectedSubject.split(',').map(id => parseInt(id)).filter(id => !isNaN(id));
        
        // If multiple subjects are selected, keep only the first one
        if (subjectIds.length > 1) {
          LoggerService.log(`[onModeChange] Exam mode: Multiple subjects selected (${subjectIds.length}), keeping only the first one`);
          const firstSubjectId = subjectIds[0];
          this.selectedSubject = firstSubjectId.toString();
          
          // Update the selectedSubjectObjects to reflect only the first subject
          const firstSubject = this.subjectList.find(s => s.id === firstSubjectId);
          if (firstSubject) {
            this.selectedSubjectObjects = [firstSubject];
          }
        }
      }
      
      // Reload subjects to ensure we have the latest exam_questions data
      if (this.selectedCategory && this.selectedSubject) {
        const user = this.authService.getUser();
        const sessionId = user?.sid;
        if (sessionId) {
          const subjects = await this.restService.getSubjects(sessionId, this.selectedCategory.id);
          if (subjects) {
            this.subjects = subjects; // Update local subjects array
            LoggerService.log(`[onModeChange] Reloaded ${subjects.length} subjects for exam mode`);
          }
          
          // Load questions count first (without filters for exam mode)
          const requestId = ++this.questionsMetaRequestSeq;
          await this.loadQuestionsMetaWithFilters(this.selectedCategory.id, this.selectedSubject, {
            redFlagMode: 'none',
            yellowFlagMode: 'none',
            greenFlagMode: 'none',
            excludeHidden: false
          }, requestId);
        }
      }
    } else {
      // In study mode, check "Don't use hidden questions" by default
      this.excludeHidden = true;

      // If a subject is already selected, ensure topics are loaded and re-apply filters
      // so questionsCount reflects the study-mode settings.
      await this.loadTopicsForSelectedSubjects();
      if (this.selectedCategory && this.selectedSubject) {
        await this.applyFilters();
      }
    }
    
    // Update question count based on new mode (after subjects and questionsCount are loaded)
    await this.updateQuestionCountForMode();
  }

  async startTest(event: Event) {
    event.preventDefault();
    
    // CRITICAL: Prevent starting a new test if one is already in progress
    // Check if we're currently on a test page (study or exam)
    const currentUrl = this.router.url;
    if (currentUrl.includes('/question-pool-study/') || currentUrl.includes('/question-pool-exam/')) {
      console.log('[startTest] Already in a test, preventing new test creation. Current URL:', currentUrl);
      alert('You are already in a test. Please finish or cancel the current test before starting a new one.');
      return;
    }
    
    // If a previous test was left “active” in memory, clear it automatically (no prompt).
    if (this.restService.currentUserTestId) {
      console.log('[startTest] Clearing stale currentUserTestId:', this.restService.currentUserTestId);
      this.restService.clearCurrentTestState();
    }
    
    const mode = this.testMode === 'exam' ? 'exam' : 'test';
    const routerLink = this.testMode === 'exam' ? this.examRouterLink : this.studyRouterLink;
    
    await this.createUserTestAndNavigate(mode, routerLink);
  }

  private async createUserTestAndNavigate(mode: string, routerLink: string[]) {
    try {
      // Always fetch fresh user test settings when starting a new test (e.g. after user changed them in Settings)
      this.restService.clearUserTestSettingsCache();

      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (!sessionId) {
        LoggerService.error('User not authenticated', null);
        return;
      }

      // Get the first subject ID (for now, we'll use the first selected subject)
      const subjectIds = this.selectedSubject ? this.selectedSubject.split(',').map(id => parseInt(id)) : [];
      const subjectId = subjectIds.length > 0 ? subjectIds[0] : null;

      if (!this.selectedCategory || !subjectId) {
        LoggerService.error('Course or subject not selected', null);
        return;
      }

      // Get exam_time_minutes from the selected subject
      const selectedSubject = this.subjects.find(s => s.subjectId === subjectId);
      const examTimeMinutes = selectedSubject?.examTimeMinutes || null;
      LoggerService.log(`[createUserTestAndNavigate] Mode: ${mode}, subjectId: ${subjectId}, examTimeMinutes: ${examTimeMinutes}`);
      if (mode === 'exam' && examTimeMinutes) {
        this.restService.currentExamTimeMinutes = examTimeMinutes;
        LoggerService.log(`[createUserTestAndNavigate] Set currentExamTimeMinutes to: ${examTimeMinutes}`);
      } else if (mode === 'exam' && !examTimeMinutes) {
        LoggerService.log(`[createUserTestAndNavigate] WARNING: Exam mode but no examTimeMinutes found for subject ${subjectId}`);
      }

      // CRITICAL: Ensure we have fresh question IDs for this new test
      // If we're starting a new test (not resuming), we need to reload question IDs from backend
      // to get a fresh random selection. Recovery-mix prefills keep a fixed ordered list.
      if (!this.restService.currentUserTestId && this.pendingQuestionIds?.length) {
        console.log('[createUserTestAndNavigate] Using pending recommended question IDs:', this.pendingQuestionIds.length);
        this.restService.setQuestionIdsForResume(this.pendingQuestionIds);
        this.restService.setMaxQuestions(this.pendingQuestionIds.length);
        this.amountQuestions = this.pendingQuestionIds.length;
        this.pendingQuestionIds = null;
      } else if (!this.restService.currentUserTestId) {
        console.log('[createUserTestAndNavigate] No currentUserTestId - reloading fresh question IDs from backend');
        let subjectIdStr: string;
        if (Array.isArray(this.selectedSubjectObjects) && this.selectedSubjectObjects.length > 0) {
          subjectIdStr = this.selectedSubjectObjects.map(s => s.id).join(',');
        } else if (this.selectedSubjectObjects && !Array.isArray(this.selectedSubjectObjects) && typeof this.selectedSubjectObjects === 'object') {
          const singleSubject = this.selectedSubjectObjects as { id: number, name: string };
          subjectIdStr = singleSubject.id.toString();
        } else {
          subjectIdStr = this.selectedSubject || '';
        }
        
        // Build filters object - MUST include onlyWrong and onlyUnseen for proper filtering
        const filters: any = {};
        if (this.redFlagMode !== 'none') filters.redFlagMode = this.redFlagMode;
        if (this.yellowFlagMode !== 'none') filters.yellowFlagMode = this.yellowFlagMode;
        if (this.greenFlagMode !== 'none') filters.greenFlagMode = this.greenFlagMode;
        if (this.excludeHidden) filters.excludeHidden = this.excludeHidden;
        // CRITICAL: Include onlyWrong and onlyUnseen filters so backend filters questions correctly
        if (this.onlyUnseen) filters.onlyUnseen = this.onlyUnseen;
        if (this.onlyWrong) filters.onlyWrong = this.onlyWrong;
        const topicIds = this.getTopicIdsFilterForRequest();
        if (topicIds !== undefined) filters.topicIds = topicIds;
        
        // Reload question IDs from backend to get fresh random selection
        const requestId = ++this.questionsMetaRequestSeq;
        await this.loadQuestionsMetaWithFilters(
          this.selectedCategory.id,
          subjectIdStr,
          Object.keys(filters).length > 0 ? filters : undefined,
          requestId
        );
        console.log('[createUserTestAndNavigate] Reloaded question IDs:', this.restService.getQuestionIds().slice(0, 10), '... (showing first 10)');
      } else {
        console.log('[createUserTestAndNavigate] currentUserTestId exists - resuming test, using existing question IDs');
      }

      // CRITICAL: setMaxQuestions must be set before getQuestionIds(), otherwise getQuestionIds()
      // returns [] when _maxQuestions is 0 (e.g. after clearCurrentTestState in constructor or startTest)
      this.restService.setMaxQuestions(this.amountQuestions);

      // Get question IDs and limit based on amountQuestions
      let questionIds = this.restService.getQuestionIds();
      if (questionIds.length > this.amountQuestions) {
        questionIds = questionIds.slice(0, this.amountQuestions);
        console.log('[createUserTestAndNavigate] Limited questionIds to', this.amountQuestions, 'questions');
      }
      console.log('[createUserTestAndNavigate] Current questionIds:', questionIds.slice(0, 10), '... (showing first 10 of', questionIds.length, ')');
      
      if (questionIds.length === 0) {
        LoggerService.error('No questions available for test', null);
        alert('No questions available. Please select a subject with questions.');
        return;
      }

      // Create user test record with all question IDs and filter settings (for recreatable tests)
      const topicIdsFilter = this.getTopicIdsFilterForRequest();
      const settings = {
        amountQuestions: this.amountQuestions,
        redFlagMode: this.redFlagMode,
        yellowFlagMode: this.yellowFlagMode,
        greenFlagMode: this.greenFlagMode,
        excludeHidden: this.excludeHidden,
        onlyUnseen: this.onlyUnseen,
        onlyWrong: this.onlyWrong,
        topicIds: topicIdsFilter,
        seedId: undefined as number | undefined
      };
      console.log('[createUserTestAndNavigate] Creating test with questionIds:', questionIds.slice(0, 10), '... (showing first 10)');
      const userTestId = await this.restService.createUserTest(
        sessionId,
        this.selectedCategory.id,
        subjectIds,
        mode,
        questionIds,
        settings
      );

      if (userTestId) {
        // Store user test ID before resetting
        console.log('[createUserTestAndNavigate] User test created:', userTestId, 'mode:', mode);
        console.log('[createUserTestAndNavigate] Test created with', questionIds.length, 'question IDs');
        this.restService.currentUserTestId = userTestId;
        this.restService.currentUserTestMode = mode;
        console.log('[createUserTestAndNavigate] Stored currentUserTestId:', this.restService.currentUserTestId);
        LoggerService.log('User test created and stored', { userTestId, mode, courseId: this.selectedCategory.id, subjectIds, questionCount: questionIds.length });
        // Reset service state for new test - this clears questionIds and questionsMap
        // BUT we need to preserve the questionIds for the test we just created
        // So we'll restore them after reset
        const savedQuestionIds = [...questionIds]; // Copy the array
        this.restService.reset();
        console.log('[createUserTestAndNavigate] After reset, restoring questionIds for navigation');
        // Restore questionIds so the exam/study component can use them
        this.restService.setQuestionIdsForResume(savedQuestionIds);
        this.restService.setMaxQuestions(savedQuestionIds.length);
        console.log('[createUserTestAndNavigate] Restored', savedQuestionIds.length, 'question IDs');
        console.log('[createUserTestAndNavigate] After reset, currentUserTestId:', this.restService.currentUserTestId);
        
        // CRITICAL: Navigate to the FIRST question from the new test, not a stale routerLink
        // The routerLink might contain a questionId from a previous test
        const firstQuestionId = savedQuestionIds[0];
        if (!firstQuestionId) {
          LoggerService.error('No question IDs available for navigation', null);
          alert('No questions available. Please try again.');
          return;
        }
        
        // Build the correct route based on mode
        const correctRouterLink = mode === 'exam' 
          ? ['/question-pool-exam/', firstQuestionId.toString(), '0']
          : ['/question-pool-study/', firstQuestionId.toString()];
        
        console.log('[createUserTestAndNavigate] Navigating to first question:', firstQuestionId, 'route:', correctRouterLink);
        // Navigate to test page with the FIRST question from the new test
        this.router.navigate(correctRouterLink);
      } else {
        console.error('[createUserTestAndNavigate] Failed to create user test');
        LoggerService.error('Failed to create user test', null);
        alert(this.restService.lastAccessDenial ? this.restService.accessDeniedMessage() : 'Failed to start test. Please try again.');
      }
    } catch (error) {
      LoggerService.error('Error starting test:', error);
      alert('An error occurred while starting the test.');
    }
  }
  
  // Helper method for template to check if amountQuestions is valid
  protected getValidAmountQuestions(): number {
    if (this.amountQuestions != null && !isNaN(this.amountQuestions) && this.amountQuestions > 0) {
      return this.amountQuestions;
    }
    return 0;
  }
  
}
